import { GA4Constants } from '../constants/analytics-constants'
import { sumGA4EventValue } from '../helpers/ga4-helpers'
import { convertCurrencyToDecimal } from '../helpers/currency-helpers'
import { Tasks } from '../interfaces/analytics-interfaces'

/**
 * Class representing aspenware platform data for GA4 events
 */
export class GA4AspenwareData {
  aspenware_platform: string
  aspenware_version: string
  aspenware_cloud_version: string | undefined
  constructor() {
    this.aspenware_platform = window?.storeSettings?.Store?.Platform ?? GA4Constants.DEFAULT_STORE_PLATFORM
    this.aspenware_version = window?.storeSettings?.Store?.AspenwareVersion
    this.aspenware_cloud_version = window?.storeSettings?.Store?.AspenwareCloudVersion
  }
}

export class GA4Error extends Error {
  constructor(public event: string, public message: string, public method: string) {
    super(message)
  }
}

export class StoreData {
  currency: string
  affiliation: string
  constructor(currency: string | undefined, affiliation: string | undefined) {
    this.currency = currency || GA4Constants.DEFAULT_CURRENCY
    this.affiliation = affiliation || GA4Constants.DEFAULT_AFFILIATION
  }
}

export class GA4ProductItem {
  item_id: string | null
  cart_item_id?: number | null
  item_list_id: string
  item_list_name: string
  discount: number
  coupon: string | null
  item_category: string
  item_category2: string | null
  item_category3: string | null
  item_name: string
  item_brand: string | null
  price: number | null
  location_id: string | null
  sku: string | null
  affiliation: string | null
  quantity: number | null
  index: number
  item_variant: string | null

  constructor(productData: {
    item_id: string | null
    cart_item_id?: number | null
    item_list_id: string | null
    item_list_name: string | null
    item_name: string | null
    discount: string | null
    coupon: string | null
    item_category: string | null
    item_category2: string | null
    item_category3: string | null
    item_variant: string | null
    item_brand: string | null
    price: number | null
    location_id: string | null
    sku: string | null
    affiliation: string | null
    quantity: number | null
    index: number
  }) {
    this.item_id = productData.item_id
    this.cart_item_id = productData.cart_item_id || null
    this.item_list_id = productData.item_list_id || GA4Constants.DEFAULT_LIST_ID
    this.item_list_name = productData.item_list_name || GA4Constants.DEFAULT_LIST_NAME
    this.item_name = productData.item_name ? productData.item_name : GA4Constants.DEFAULT_UNKNOWN
    this.discount = productData.discount ? convertCurrencyToDecimal(productData.discount) : 0
    this.coupon = productData.coupon
    this.item_category = productData.item_category || GA4Constants.DEFAULT_CATEGORY
    this.item_category2 = productData.item_category2
    this.item_category3 = productData.item_category3
    this.item_variant = productData.item_variant || null
    this.item_brand = productData.item_brand
    this.price = productData.price ? convertCurrencyToDecimal(productData.price) : null
    this.location_id = productData.location_id
    this.sku = productData.sku || null
    this.affiliation = productData.affiliation
    this.quantity = productData.quantity ? productData.quantity : 0
    this.index = productData.index ? productData.index : 0
  }
}

/** Class representing a GA4 ecommerce data object with transaction Information */
export class TransactionData {
  transactionId: string
  transactionAffiliation: string
  transactionTotal: number
  transactionTax: number
  transactionShipping: number
  transactionDueOnArrival: number
  transactionProducts: []

  constructor(
    transactionId: string,
    transactionAffiliation: string,
    transactionTotal: number,
    transactionTax: number,
    transactionShipping: number,
    transactionDueOnArrival: number,
    transactionProducts: []
  ) {
    this.transactionId = transactionId
    this.transactionAffiliation = transactionAffiliation
    this.transactionTotal = transactionTotal
    this.transactionTax = transactionTax
    this.transactionShipping = transactionShipping
    this.transactionDueOnArrival = transactionDueOnArrival
    this.transactionProducts = transactionProducts
  }
}

/** Class representing a GA4 ecommerce data object with transaction Information */
export class GA4TransactionEventData {
  event: string
  currency: string
  aspenware_data: GA4AspenwareData
  transactionId: string
  transactionAffiliation: string
  transactionTotal: number
  transactionTax: number
  transactionShipping: number
  transactionDueOnArrival: number
  transactionProducts: []

  constructor(eventName: string, storeData: StoreData, transactionData: TransactionData) {
    this.event = eventName
    this.currency = storeData.currency
    this.transactionId = transactionData.transactionId
    this.transactionAffiliation = transactionData.transactionAffiliation
    this.transactionTotal = transactionData.transactionTotal
    this.transactionTax = transactionData.transactionTax
    this.transactionShipping = transactionData.transactionShipping
    this.transactionDueOnArrival = transactionData.transactionDueOnArrival
    this.transactionProducts = transactionData.transactionProducts
    this.aspenware_data = new GA4AspenwareData()
  }
}

/** Class representing a GA4 event data object */
export class GA4EventData {
  event: string
  ecommerce: GA4EcommData | {}
  aspenware_data: GA4AspenwareData
  constructor(event: string, ecommerce: GA4EcommData) {
    this.event = event
    this.ecommerce = ecommerce ? ecommerce : {}
    this.aspenware_data = new GA4AspenwareData()
  }
}

export class GA4EventTaskData extends GA4EventData {
  task_list?: Tasks[]
  constructor(event: string, ecommerce: GA4EcommData, task_list: Tasks[] | []) {
    super(event, ecommerce)
    this.task_list = task_list
  }
}

export class GA4EventMediaData extends GA4EventData {
  media_card_count?: number | undefined
  constructor(event: string, ecommerce: GA4EcommData, mediaCardCount: number | undefined) {
    super(event, ecommerce)
    this.media_card_count = mediaCardCount
  }
}

/** Class representing a GA4 ecommerce data object */
export class GA4EcommData {
  currency: string
  items: GA4ProductItem[]
  value: number

  constructor(storeData: StoreData, eCommItems: GA4ProductItem[]) {
    // Sum the value of this eCommerce transaction as the price of all the items involved multiplied by quantity if present
    const eCommValue = sumGA4EventValue(eCommItems)

    this.currency = storeData.currency
    this.items = eCommItems ? eCommItems : []
    this.value = eCommValue ? eCommValue : 0
  }
}

/** Class representing a GA4 ecommerce data object for purchase events */
export class GA4PurchaseEcommData extends GA4EcommData {
  transaction_id?: string | null
  customerBillingEmail?: string | null
  customerBillingPhone?: string | null
  tax?: number
  shipping?: number
  payment_type?: string
  constructor(
    storeData: StoreData,
    eCommItems: GA4ProductItem[],
    transaction_id?: string | null,
    customerBillingEmail?: string | null,
    customerBillingPhone?: string | null,
    tax?: number,
    shipping?: number,
    paymentType?: string | undefined
  ) {
    super(storeData, eCommItems)

    this.transaction_id = transaction_id
    this.customerBillingEmail = customerBillingEmail
    this.customerBillingPhone = customerBillingPhone
    this.tax = tax
    this.shipping = shipping
    this.payment_type = paymentType || GA4Constants.DEFAULT_PAYMENT_TYPE
  }
}

export class GA4PaymentEcommData extends GA4EcommData {
  payment_type?: string
  constructor(storeData: StoreData, eCommItems: GA4ProductItem[], paymentType?: string | undefined) {
    super(storeData, eCommItems)
    this.payment_type = paymentType || GA4Constants.DEFAULT_PAYMENT_TYPE
  }
}

export type GA4ShadowCart = Record<number, GA4ProductItem[]>

export class GA4CaptureErrorEventData {
  event: string
  errorType: string
  errorMessage: string
  aspenware_data: GA4AspenwareData
  constructor(event: string, errorType: string, errorMessage: string) {
    this.event = event
    this.errorType = errorType
    this.errorMessage = errorMessage
    this.aspenware_data = new GA4AspenwareData()
  }
}
